import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { Order } from '@/types/commerce'
import { fetchActiveOrders } from '@/services/fetch-active-orders'
import { QUERY_KEYS } from '@/constants/queryKeys'

// Every query option except the ones a consumer must not own. queryKey/queryFn and the
// two hash options decide which cache entry this is — queryClient only derives a hash
// `if (!defaultedOptions.queryHash)`, so a supplied one wins outright and forks the
// entry this hook exists to centralize. initialData* would seed that shared entry with
// one caller's snapshot for every other observer.
type ActiveOrdersOptions = Omit<
  UseQueryOptions<Order[]>,
  'queryKey' | 'queryFn' | 'queryKeyHashFn' | 'queryHash' | 'initialData' | 'initialDataUpdatedAt'
>

/**
 * Single source for the shared active-orders cache entry (QUERY_KEYS.activeOrders,
 * dehydrated from SSR in prepare-app-state). All consumers (usePurchase,
 * useAwaitOrder, AppInitializer branding derivation) observe this one entry
 * instead of defining their own copies of the query.
 */
export const useActiveOrders = (options?: ActiveOrdersOptions) => {
  return useQuery({
    queryKey: QUERY_KEYS.activeOrders,
    queryFn: () => fetchActiveOrders(),
    ...options,
  })
}
